import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ledgerService, userService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box, Button, Typography, Container, Paper, Table, TableBody, TableCell, TextField, TableContainer, TableHead, TableRow, Alert, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, InputLabel, Select, MenuItem, Menu
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [actionInProgress, setActionInProgress] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const { currentUser } = useAuth();
  const [statusFilter, setStatusFilter] = useState('active');
  const [anchorEl, setAnchorEl] = useState(null);
  const openStatusMenu = Boolean(anchorEl);

  const [roleFilter, setRoleFilter] = useState('all');
  const [anchorElRole, setAnchorElRole] = useState(null);
  const openRoleMenu = Boolean(anchorElRole);

  const [balancesByUserId, setBalancesByUserId] = useState({});

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createdTempPassword, setCreatedTempPassword] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [newUser, setNewUser] = useState({
    email: '', first_name: '', last_name: '', role: 'user',
    phone_number: '', city: '', address: '', street_number: '', fiscal_code: ''
  });

  const handleOpenStatusMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseStatusMenu = () => {
    setAnchorEl(null);
  };

  const handleStatusSelect = (value) => {
    setStatusFilter(value);
    handleCloseStatusMenu();
  };

  const handleOpenRoleMenu = (event) => {
    setAnchorElRole(event.currentTarget);
  };

  const handleCloseRoleMenu = () => {
    setAnchorElRole(null);
  };

  const handleRoleSelect = (value) => {
    setRoleFilter(value);
    handleCloseRoleMenu();
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setRoleFilter('all');
    setAnchorEl(null);
    setAnchorElRole(null);
  };

  // Carica gli utenti
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const [usersRes, accountsRes] = await Promise.all([
          userService.getUsers(),
          ledgerService.getAllAccounts(),
        ]);
        setUsers(usersRes.data);
        const map = {};
        for (const a of accountsRes.data || []) {
          map[String(a.user)] = a.balance_hours;
        }
        setBalancesByUserId(map);
        setLoading(false);
      } catch (err) {
        setError('Errore nel caricamento degli utenti');
        setLoading(false);
        console.error(err);
      }
    };

    fetchUsers();
  }, []);

  // Apre il dialog per cambiare il ruolo
  const handleOpenDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setDialogOpen(true);
  };

  // Chiude il dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  // Apre/chiude dialog di conferma eliminazione
  const handleOpenDeleteDialog = (user) => {
    if (!canDeleteUser(user)) {
      setError('Non puoi eliminare te stesso o utenti con ruolo amministratore/moderatore.');
      return;
    }
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  // Gestisce il cambio di ruolo
  const handleRoleChange = async () => {
    if (!selectedUser || newRole === selectedUser.role) {
      handleCloseDialog();
      return;
    }

    setActionInProgress(selectedUser.id);
    setError('');
    setSuccessMessage('');

    try {
      await userService.updateUser(selectedUser.id, { role: newRole });

      // Aggiorna la lista degli utenti
      setUsers(users.map(user =>
        user.id === selectedUser.id ? { ...user, role: newRole } : user
      ));

      setSuccessMessage(`Ruolo dell'utente ${selectedUser.email} aggiornato con successo.`);
      handleCloseDialog();
    } catch (err) {
      setError('Errore nell\'aggiornamento del ruolo');
      console.error(err);
    }

    setActionInProgress(null);
  };

  // Gestisce la disattivazione/attivazione di un utente
  const handleToggleActive = async (user) => {
    // I moderatori non possono attivare o disattivare un amministratore
    if (currentUser?.role === 'moderator' && user.role === 'admin') {
      setError('Non puoi modificare lo stato di un amministratore.');
      return;
    }
    setActionInProgress(user.id);
    setError('');
    setSuccessMessage('');

    try {
      await userService.updateUser(user.id, { is_active: !user.is_active });

      // Aggiorna la lista degli utenti
      setUsers(users.map(u =>
        u.id === user.id ? { ...u, is_active: !u.is_active } : u
      ));

      setSuccessMessage(`Utente ${user.email} ${user.is_active ? 'disattivato' : 'attivato'} con successo.`);
    } catch (err) {
      setError(`Errore nella ${user.is_active ? 'disattivazione' : 'attivazione'} dell'utente`);
      console.error(err);
    }

    setActionInProgress(null);
  };

  // Traduce il ruolo in italiano
  const translateRole = (role) => {
    switch (role) {
      case 'superadmin':
        return 'Super Amministratore';
      case 'admin':
        return 'Amministratore';
      case 'moderator':
        return 'Moderatore';
      case 'user':
        return 'Utente';
      default:
        return role;
    }
  };

  // Verifica se l'utente corrente (admin) può eliminare il target
  const canDeleteUser = (user) => {
    return (
      (currentUser?.role === 'admin' || currentUser?.role === 'superadmin') &&
      user.role === 'user' &&
      user.id !== currentUser?.id
    );
  };

  // Filtra gli utenti in base alla ricerca
  const filteredUsers = users.filter((user) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      user.first_name?.toLowerCase().includes(query) ||
      user.last_name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      translateRole(user.role).toLowerCase().includes(query);

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active);

    return matchesSearch && matchesStatus && (roleFilter === 'all' || user.role === roleFilter);
  });

  const roleFilterLabel = roleFilter === 'all'
    ? 'Tutti'
    : roleFilter === 'user'
      ? 'Utente'
      : roleFilter === 'moderator'
        ? 'Moderatore'
        : roleFilter === 'admin'
          ? 'Amministratore'
          : 'Super Amministratore';

  const statusFilterLabel = statusFilter === 'all'
    ? 'Tutti'
    : statusFilter === 'active'
      ? 'Attivi'
      : 'Disattivati';

  if (loading) {
    return (
      <Container component="main" maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }


  return (
    <Container component="main" maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>

                <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'center', position: 'relative', mb: 3 }} >
          <Button  component={Link} to="/dashboard" startIcon={<ArrowBackIcon />} variant="outlined" color="primary"
            sx={{ alignSelf: { xs: 'flex-start', sm: 'flex-start' }, mb: { xs: 1, sm: 0 }, textTransform: 'none', }} >
            Dashboard
          </Button>

          <Typography component="h1" variant="h4" gutterBottom
            sx={{ textAlign: { xs: 'left', sm: 'center' }, width: '100%', }} >
            Gestione Utenti
          </Typography>

          {(currentUser?.role === 'superadmin') && (
            <Button
              variant="contained"
              color="primary"
              sx={{ position: { sm: 'absolute' }, right: { sm: 24 }, top: { sm: 24 }, textTransform: 'none' }}
              onClick={() => { setCreateDialogOpen(true); setCreatedTempPassword(''); setNewUser({ email: '', first_name: '', last_name: '', role: 'user', phone_number: '', city: '', address: '', street_number: '', fiscal_code: '' }); }}
            >
              Nuovo Utente
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 3 }}>
          <TextField
            fullWidth
            label="Cerca per nome, email, ruolo o stato"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1, minWidth: { xs: '100%', md: 320 } }}
          />
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<FilterListIcon fontSize="small" />}
            onClick={handleOpenRoleMenu}
            aria-label="Filtro ruolo"
            sx={{
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' },
              minWidth: 0,
              whiteSpace: 'normal',
              lineHeight: 1.15,
              px: 1.25,
              py: 0.75,
              justifyContent: 'flex-start',
              textAlign: 'left',
              '& .MuiButton-startIcon': { ml: 0, mr: 1 },
            }}
          >
            Ruolo: {roleFilterLabel}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<FilterListIcon fontSize="small" />}
            onClick={handleOpenStatusMenu}
            aria-label="Filtro stato"
            sx={{
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' },
              minWidth: 0,
              whiteSpace: 'normal',
              lineHeight: 1.15,
              px: 1.25,
              py: 0.75,
              justifyContent: 'flex-start',
              textAlign: 'left',
              '& .MuiButton-startIcon': { ml: 0, mr: 1 },
            }}
          >
            Stato: {statusFilterLabel}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<RefreshIcon fontSize="small" />}
            onClick={handleResetFilters}
            aria-label="Reset filtri"
            sx={{
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' },
              minWidth: 0,
              whiteSpace: 'normal',
              lineHeight: 1.15,
              px: 1.25,
              py: 0.75,
              justifyContent: 'flex-start',
              textAlign: 'left',
              '& .MuiButton-startIcon': { ml: 0, mr: 1 },
            }}
          >
            Reset filtri
          </Button>
        </Box>
        <Menu
          anchorEl={anchorElRole}
          open={openRoleMenu}
          onClose={handleCloseRoleMenu}
        >
          <MenuItem selected={roleFilter === 'all'} onClick={() => handleRoleSelect('all')}>
            Tutti
          </MenuItem>
          <MenuItem selected={roleFilter === 'user'} onClick={() => handleRoleSelect('user')}>
            Utente
          </MenuItem>
          <MenuItem selected={roleFilter === 'moderator'} onClick={() => handleRoleSelect('moderator')}>
            Moderatore
          </MenuItem>
          <MenuItem selected={roleFilter === 'admin'} onClick={() => handleRoleSelect('admin')}>
            Amministratore
          </MenuItem>
          {currentUser?.role === 'superadmin' && (
            <MenuItem selected={roleFilter === 'superadmin'} onClick={() => handleRoleSelect('superadmin')}>
              Super Amministratore
            </MenuItem>
          )}
        </Menu>
        <Menu
          anchorEl={anchorEl}
          open={openStatusMenu}
          onClose={handleCloseStatusMenu}
        >
          <MenuItem selected={statusFilter === 'all'} onClick={() => handleStatusSelect('all')}>
            Tutti
          </MenuItem>
          <MenuItem selected={statusFilter === 'active'} onClick={() => handleStatusSelect('active')}>
            Attivo
          </MenuItem>
          <MenuItem selected={statusFilter === 'inactive'} onClick={() => handleStatusSelect('inactive')}>
            Disattivato
          </MenuItem>
        </Menu>

        {!isMobile ? (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Cognome</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Saldo (h)</TableCell>
                <TableCell>Ruolo</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell>Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.first_name}</TableCell>
                    <TableCell>{user.last_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {balancesByUserId[String(user.id)] !== undefined ? (
                        <Typography color={Number(balancesByUserId[String(user.id)]) < 0 ? 'error' : 'inherit'}>
                          {balancesByUserId[String(user.id)]} h
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{translateRole(user.role)}</TableCell>
                    <TableCell>
                      {user.is_active ? (
                        <Typography color="primary">Attivo</Typography>
                      ) : (
                        <Typography color="error">Disattivato</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handleOpenDialog(user)}
                            disabled={actionInProgress === user.id}
                          >
                            Cambia Ruolo
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          color={user.is_active ? "error" : "success"}
                          size="small"
                          onClick={() => handleToggleActive(user)}
                          disabled={
                            actionInProgress === user.id ||
                            (currentUser?.role === 'moderator' && user.role === 'admin')
                          }
                        >
                          {actionInProgress === user.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            user.is_active ? 'Disattiva' : 'Attiva'
                          )}
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          component={Link}
                          to={`/admin/users/${user.id}`}
                        >
                          Dettagli
                        </Button>
                        {canDeleteUser(user) && (
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={() => handleOpenDeleteDialog(user)}
                            disabled={actionInProgress === user.id}
                          >
                            Elimina
                          </Button>
                        )}
                      </Box>
                    </TableCell>

                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body1" align="center" sx={{ mt: 4 }}>
                      Nessun utente trovato.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        ) : (
          <Box sx={{ display: 'grid', gap: 2 }}>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <Paper key={user.id} variant="outlined" sx={{ p: 2, minWidth: 0, overflow: 'hidden' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {user.first_name} {user.last_name}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    {user.email}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'grid', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Saldo: {balancesByUserId[String(user.id)] !== undefined ? `${balancesByUserId[String(user.id)]} h` : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      Ruolo: {translateRole(user.role)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      Stato: {user.is_active ? 'Attivo' : 'Disattivato'}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
                    {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleOpenDialog(user)}
                        disabled={actionInProgress === user.id}
                        fullWidth
                        sx={{ whiteSpace: 'normal', lineHeight: 1.15, py: 0.75, px: 1.25, minWidth: 0 }}
                      >
                        Cambia Ruolo
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color={user.is_active ? "error" : "success"}
                      size="small"
                      onClick={() => handleToggleActive(user)}
                      disabled={
                        actionInProgress === user.id ||
                        (currentUser?.role === 'moderator' && user.role === 'admin')
                      }
                      fullWidth
                      sx={{ whiteSpace: 'normal', lineHeight: 1.15, py: 0.75, px: 1.25, minWidth: 0 }}
                    >
                      {actionInProgress === user.id ? <CircularProgress size={20} /> : (user.is_active ? 'Disattiva' : 'Attiva')}
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      component={Link}
                      to={`/admin/users/${user.id}`}
                      fullWidth
                      sx={{ whiteSpace: 'normal', lineHeight: 1.15, py: 0.75, px: 1.25, minWidth: 0 }}
                    >
                      Dettagli
                    </Button>
                    {canDeleteUser(user) && (
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleOpenDeleteDialog(user)}
                        disabled={actionInProgress === user.id}
                        fullWidth
                        sx={{ whiteSpace: 'normal', lineHeight: 1.15, py: 0.75, px: 1.25, minWidth: 0 }}
                      >
                        Elimina
                      </Button>
                    )}
                  </Box>
                </Paper>
              ))
            ) : (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="body1" align="center">
                  Nessun utente trovato.
                </Typography>
              </Paper>
            )}
          </Box>
        )}
      </Paper>

      {/* Dialog crea utente */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Crea Nuovo Utente</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Inserisci i dati dell'utente. Se non specifichi una password, verrà generata una password temporanea.
          </DialogContentText>
          <TextField sx={{ mt: 2 }} label="Email" fullWidth value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField label="Nome" fullWidth value={newUser.first_name} onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })} />
            <TextField label="Cognome" fullWidth value={newUser.last_name} onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })} />
          </Box>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="create-role-select-label">Ruolo</InputLabel>
            <Select
              labelId="create-role-select-label"
              id="create-role-select"
              value={newUser.role}
              label="Ruolo"
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
            >
              {currentUser?.role === 'superadmin' && <MenuItem value="superadmin">Super Amministratore</MenuItem>}
              <MenuItem value="admin">Amministratore</MenuItem>
              <MenuItem value="moderator">Moderatore</MenuItem>
              <MenuItem value="user">Utente</MenuItem>
            </Select>
          </FormControl>
          {createdTempPassword && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Password temporanea: <strong>{createdTempPassword}</strong>
              <Button size="small" sx={{ ml: 2 }} onClick={() => navigator.clipboard.writeText(createdTempPassword)}>Copia</Button>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Annulla</Button>
          <Button
            onClick={async () => {
              setCreateLoading(true);
              setError('');
              setSuccessMessage('');
              setCreatedTempPassword('');
              try {
                const res = await userService.createUser({
                  email: newUser.email,
                  first_name: newUser.first_name,
                  last_name: newUser.last_name,
                  role: newUser.role,
                  phone_number: newUser.phone_number,
                  city: newUser.city,
                  address: newUser.address,
                  street_number: newUser.street_number,
                  fiscal_code: newUser.fiscal_code,
                });
                const created = res.data;
                setUsers(prev => [created, ...prev]);
                setSuccessMessage(`Utente ${created.email} creato con successo.`);
                if (created.temp_password) setCreatedTempPassword(created.temp_password);
              } catch (err) {
                setError('Errore nella creazione dell\'utente');
                console.error(err);
              }
              setCreateLoading(false);
            }}
            variant="contained"
            color="primary"
            disabled={createLoading || !newUser.email}
          >
            Crea
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per il cambio ruolo */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Cambia Ruolo Utente</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Seleziona il nuovo ruolo per l'utente {selectedUser?.email}:
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="role-select-label">Ruolo</InputLabel>
            <Select
              labelId="role-select-label"
              id="role-select"
              value={newRole}
              label="Ruolo"
              onChange={(e) => setNewRole(e.target.value)}
            >
              {currentUser?.role === 'superadmin' && <MenuItem value="superadmin">Super Amministratore</MenuItem>}
              <MenuItem value="admin">Amministratore</MenuItem>
              <MenuItem value="moderator">Moderatore</MenuItem>
              <MenuItem value="user">Utente</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button onClick={handleRoleChange} variant="contained" color="primary">
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog di conferma eliminazione */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare definitivamente l'utente {userToDelete?.email}? Questa azione è irreversibile.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annulla</Button>
          <Button
            onClick={async () => {
              if (!userToDelete) return;
              setActionInProgress(userToDelete.id);
              setError('');
              setSuccessMessage('');
              try {
                await userService.deleteUser(userToDelete.id);
                setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
                setSuccessMessage(`Utente ${userToDelete.email} eliminato definitivamente.`);
              } catch (err) {
                const detail = err?.response?.data?.detail;
                setError(detail || 'Errore nell\'eliminazione dell\'utente');
                console.error(err);
              }
              setActionInProgress(null);
              handleCloseDeleteDialog();
            }}
            variant="contained"
            color="error"
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagement;
